import { Box, Link, Stack, Tag, TagCloseButton, useColorModeValue } from '@chakra-ui/react';
import { joinUntilMaxLength } from '@main/shared/utils';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { Combobox, ComboboxProps, selectValueToArray, withMultiCombobox } from '../../combobox';
import { NavigateLink, type NavigateTarget } from '../../navigate-link';
import { OverflowContainer } from '../../overflow-tooltip';
import { createOptionColumnHelper } from '../shared/select';

export type RelationOption = {
  value: string;
  label?: string | null;
  navigate?: NavigateTarget;
  onNavigate?: () => void;
};

const TOOLTIP_MAX_CHARS = 500;

/** Same purple link treatment as table link columns (`RenderLink`). */
const relationLinkProps = {
  color: 'purple.400',
} as const;

const getOptionValue = (option: RelationOption) => option.value;
const getOptionLabel = (option: RelationOption) => option.label || option.value;

function RelationOptionLabel(option: RelationOption) {
  const displayLabel = getOptionLabel(option);

  if (option.navigate) {
    return (
      <NavigateLink
        navigate={option.navigate}
        onClick={(event) => {
          event.stopPropagation();
          option.onNavigate?.();
        }}
        display="inline"
        {...relationLinkProps}
      >
        {displayLabel}
      </NavigateLink>
    );
  }

  /* Link-styled but not navigable, matching RenderLink's treatment; a real
   * ComplyKit Link is on the component map. */
  return (
    <Link as="span" display="inline" {...relationLinkProps}>
      {displayLabel}
    </Link>
  );
}

function RelationFilterOption({
  removeProps,
  ...option
}: RelationOption & {
  menuIsOpen?: boolean;
  removeProps?: JSX.IntrinsicElements['div'];
}) {
  const label = getOptionLabel(option);

  return (
    <Tag bg="none" minW="auto" maxW="100%" px={0}>
      <OverflowContainer.Tooltip label={label} fontSize="sm" openDelay={500} placement="top-start">
        <Link as="span" display="block" minW={0} maxW="100%" isTruncated {...relationLinkProps}>
          {label}
        </Link>
      </OverflowContainer.Tooltip>
      {removeProps && <Box as={TagCloseButton} {...removeProps} aria-label={`Remove ${label}`} />}
    </Tag>
  );
}

/** Filter combobox for relation options. Cells are read-only for now. */
function EditableRelation<TOption extends RelationOption, IsMulti extends boolean = false>(
  props: ComboboxProps<TOption, IsMulti>,
) {
  const multiComboboxProps = withMultiCombobox(props);

  return (
    <Combobox
      getOptionValue={getOptionValue}
      getOptionLabel={getOptionLabel}
      {...multiComboboxProps}
      components={{
        Option: RelationFilterOption,
        ...props.components,
      }}
    />
  );
}

export function RelationPreview({ items }: { items: readonly RelationOption[] }) {
  const { t } = useTranslation('ui');
  const tooltipBgColor = useColorModeValue('gray.700', 'gray.900');
  const { parts: tooltipParts, remaining: tooltipRemaining } = joinUntilMaxLength(
    items.map(getOptionLabel),
    TOOLTIP_MAX_CHARS,
    '\n',
  );

  return (
    <OverflowContainer>
      <OverflowContainer.Tooltip
        label={
          <Stack py={2} px={1} spacing={1}>
            {tooltipParts.map((label, index) => (
              <span key={`${label}-${index}`}>{label}</span>
            ))}
            {tooltipRemaining > 0 && (
              <span>{`... ${t('table.column.relation.andMore', { count: tooltipRemaining })}`}</span>
            )}
          </Stack>
        }
        placement="top-start"
        fontSize="sm"
        openDelay={500}
        bgColor={tooltipBgColor}
        maxW="md"
      >
        <Box py={4} px={2} w="100%" minW={0} isTruncated>
          {items.map((data, index) => (
            <Fragment key={data.value}>
              {index > 0 && ', '}
              <RelationOptionLabel {...data} />
            </Fragment>
          ))}
        </Box>
      </OverflowContainer.Tooltip>
    </OverflowContainer>
  );
}

export const relation = createOptionColumnHelper<RelationOption>({
  columnType: 'relation',
  getOptionValue,
  getOptionLabel,

  components: {
    Preview: ({ value }) => <RelationPreview items={selectValueToArray(value)} />,

    Edit: EditableRelation,
  },
});
